import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { IChatbot, Position } from '@/types/chatbot-types'
import type { ISiddDuoConfiguration } from '@/types/component-configurations'
import type { IChatbotState } from './types'

export function getOrCreateChatbotStore(storeKey: string) {
  return defineStore(`chatbot-${storeKey}`, () => {
    const state = ref<IChatbotState>({
      id: null,
      tenantId: null,
      createdAt: null,
      modifiedAt: null,
      configuration: null,
      brand: null,
      defaultQuestions: [],
      siddDuoConfig: null,
      authToken: null,
      //
      isLoading: false,
      error: null,
      isInitialized: false,
      lastLoaded: null,
    })

    const id = computed(() => state.value.id)
    const tenantId = computed(() => state.value.tenantId)
    const createdAt = computed(() => state.value.createdAt)
    const modifiedAt = computed(() => state.value.modifiedAt)

    const configuration = computed(() => {
      const baseConfig = state.value.configuration
      if (!baseConfig) return null

      const siddDuoShowsButton =
        state.value.siddDuoConfig?.headerConfiguration?.rightSection
          ?.showSourcesButton

      if (siddDuoShowsButton !== undefined) {
        baseConfig.showSourceAttribution = siddDuoShowsButton
      }

      return baseConfig
    })

    const brand = computed(() => {
      const baseBrand = state.value.brand
      const siddDuoTitle =
        state.value.siddDuoConfig?.headerConfiguration?.centerSection?.title

      if (siddDuoTitle && baseBrand) {
        return {
          ...baseBrand,
          name: siddDuoTitle,
        }
      }

      return baseBrand
    })

    const defaultQuestions = computed(() => {
      const siddDuoQuestions =
        state.value.siddDuoConfig?.welcomeConfiguration?.questions

      if (siddDuoQuestions && siddDuoQuestions.length > 0) {
        return siddDuoQuestions
      }

      return state.value.defaultQuestions
    })

    const authToken = computed(() => state.value.authToken)
    const isLoading = computed(() => state.value.isLoading)
    const error = computed(() => state.value.error)
    const isInitialized = computed(() => state.value.isInitialized)
    const lastLoaded = computed(() => state.value.lastLoaded)
    const siddDuoConfig = computed(() => state.value.siddDuoConfig)

    const setChatbot = (chatbotData: IChatbot): void => {
      state.value.id = chatbotData.id
      state.value.tenantId = chatbotData.tenantId
      state.value.createdAt = chatbotData.createdAt
      state.value.modifiedAt = chatbotData.modifiedAt
      state.value.configuration = chatbotData.configuration || null
      state.value.brand = chatbotData.brand || null
      state.value.defaultQuestions = chatbotData.defaultQuestions || []

      state.value.lastLoaded = new Date()
      state.value.isInitialized = true
      state.value.error = null
    }

    const setLoading = (loading: boolean): void => {
      state.value.isLoading = loading
    }

    const setError = (error: string | null): void => {
      state.value.error = error
    }

    const setAuthToken = (token: string | null): void => {
      state.value.authToken = token
    }

    const clearAuth = (): void => {
      state.value.authToken = null
    }

    const setTenantId = (tenantId: string | null): void => {
      state.value.tenantId = tenantId
    }

    const setSiddDuoConfig = (config: ISiddDuoConfiguration | null): void => {
      state.value.siddDuoConfig = config
    }

    const applyOverrideConfiguration = (overrides: {
      position?: Position
      autoOpen?: boolean
      isActive?: boolean
    }): void => {
      if (!state.value.configuration) return

      state.value.configuration = {
        ...state.value.configuration,
        ...(overrides.position !== undefined && {
          position: overrides.position,
        }),
        ...(overrides.autoOpen !== undefined && {
          autoOpen: overrides.autoOpen,
        }),
        ...(overrides.isActive !== undefined && {
          isActive: overrides.isActive,
        }),
      }
    }

    const reset = (): void => {
      state.value.id = null
      state.value.tenantId = null
      state.value.createdAt = null
      state.value.modifiedAt = null
      state.value.configuration = null
      state.value.brand = null
      state.value.defaultQuestions = []
      state.value.siddDuoConfig = null
      state.value.authToken = null

      state.value.isLoading = false
      state.value.error = null
      state.value.lastLoaded = null
      state.value.isInitialized = false
    }

    return {
      // data
      id,
      tenantId,
      createdAt,
      modifiedAt,
      configuration,
      brand,
      defaultQuestions,
      siddDuoConfig,
      authToken,

      // State
      isLoading,
      error,
      isInitialized,
      lastLoaded,

      // Actions
      setChatbot,
      setLoading,
      setError,
      setAuthToken,
      clearAuth,
      setTenantId,
      setSiddDuoConfig,
      applyOverrideConfiguration,
      reset,
    }
  })
}

export type ChatbotStore = ReturnType<typeof getOrCreateChatbotStore>
